/**
 * File drop zone component for DEX UI.
 * Supports drag-and-drop and click-to-browse with size validation.
 */

// Conservative limit for DynamoDB (accounting for base64 + sealed box overhead)
const MAX_FILE_SIZE_KB = 285;

/**
 * Validate a file for size constraints.
 * @param {File} file - File to validate
 * @returns {{valid: boolean, error?: string}}
 */
function validateFile(file) {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  const fileSizeKB = Math.round(file.size / 1024);
  if (file.size > MAX_FILE_SIZE_KB * 1024) {
    return {
      valid: false,
      error: `File too large (${fileSizeKB}KB). Maximum is ${MAX_FILE_SIZE_KB}KB.`
    };
  }

  return { valid: true };
}

/**
 * Format file size for display.
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes}B`;
  return `${Math.round(bytes / 1024)}KB`;
}

/**
 * Render the file drop zone into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} config - Configuration object
 * @param {File|null} config.file - Currently selected file (or null)
 * @param {string} [config.fileName] - File name to display (defaults to file.name)
 * @param {string|null} [config.error] - Error message to display
 * @param {boolean} [config.disabled] - Whether the drop zone is disabled
 */
export function renderFileDrop(container, config = {}) {
  const { file = null, fileName = null, error = null, disabled = false } = config;

  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed pointer-events-none'
    : 'cursor-pointer hover:border-blue-500';

  const displayName = fileName || (file ? file.name : null);
  const displaySize = file ? formatFileSize(file.size) : null;

  container.innerHTML = `
    <div
      id="file-drop-zone"
      class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-colors ${disabledClasses}"
    >
      <input type="file" id="file-input" class="hidden" ${disabled ? 'disabled' : ''} />

      ${file ? `
        <div class="flex items-center justify-center gap-3">
          <svg class="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <div class="text-left">
            <p class="text-sm font-medium text-gray-700 truncate max-w-xs">${displayName}</p>
            <p class="text-xs text-gray-500">${displaySize}</p>
          </div>
          <button
            type="button"
            id="file-clear-btn"
            class="ml-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            aria-label="Remove file"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      ` : `
        <svg class="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
        </svg>
        <p class="mt-2 text-sm text-gray-600">Drag and drop a file here</p>
        <p class="text-xs text-gray-400">or click to browse</p>
      `}

      <p class="text-gray-400 text-xs mt-2">Max size: ${MAX_FILE_SIZE_KB}KB</p>
    </div>
    ${error ? `<p class="text-red-500 text-xs mt-1">${error}</p>` : ''}
  `;
}

/**
 * Initialize file drop zone with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onFile - Called when file is selected/dropped: (file, error) => void
 * @param {Function} callbacks.onClear - Called when file is cleared: () => void
 * @returns {Function} Cleanup function that removes event listeners
 */
export function initFileDrop(container, callbacks = {}) {
  const { onFile, onClear } = callbacks;

  // Get references to elements
  const dropZone = container.querySelector('#file-drop-zone');
  const fileInput = container.querySelector('#file-input');
  const clearBtn = container.querySelector('#file-clear-btn');

  if (!dropZone || !fileInput) {
    console.warn('File drop zone elements not found');
    return () => {};
  }

  // Handler functions for event listener management
  function handleClick(e) {
    // Don't trigger file input if clicking the clear button
    if (e.target.closest('#file-clear-btn')) return;
    fileInput.click();
  }

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  }

  function handleDragEnter(e) {
    e.preventDefault();
    dropZone.classList.add('border-blue-500', 'bg-blue-50');
  }

  function handleDragOver(e) {
    e.preventDefault();
    dropZone.classList.add('border-blue-500', 'bg-blue-50');
  }

  function handleDragLeave(e) {
    e.preventDefault();
    dropZone.classList.remove('border-blue-500', 'bg-blue-50');
  }

  function handleDrop(e) {
    e.preventDefault();
    dropZone.classList.remove('border-blue-500', 'bg-blue-50');

    const file = e.dataTransfer?.files?.[0];
    if (file) {
      processFile(file);
    }
  }

  function handleClear() {
    // Reset file input
    fileInput.value = '';
    if (onClear) {
      onClear();
    }
  }

  function processFile(file) {
    const validation = validateFile(file);
    if (validation.valid) {
      if (onFile) {
        onFile(file, null);
      }
    } else {
      if (onFile) {
        onFile(null, validation.error);
      }
    }
  }

  // Attach event listeners
  dropZone.addEventListener('click', handleClick);
  fileInput.addEventListener('change', handleFileSelect);
  dropZone.addEventListener('dragenter', handleDragEnter);
  dropZone.addEventListener('dragover', handleDragOver);
  dropZone.addEventListener('dragleave', handleDragLeave);
  dropZone.addEventListener('drop', handleDrop);

  if (clearBtn) {
    clearBtn.addEventListener('click', handleClear);
  }

  // Return cleanup function
  return function cleanup() {
    dropZone.removeEventListener('click', handleClick);
    fileInput.removeEventListener('change', handleFileSelect);
    dropZone.removeEventListener('dragenter', handleDragEnter);
    dropZone.removeEventListener('dragover', handleDragOver);
    dropZone.removeEventListener('dragleave', handleDragLeave);
    dropZone.removeEventListener('drop', handleDrop);

    if (clearBtn) {
      clearBtn.removeEventListener('click', handleClear);
    }
  };
}
